// Onboarding et déverrouillage de la clé maîtresse K.
//
// K est le pivot de tout le chiffrement local (états MLS, documents, notes).
// Elle est **aléatoire et stable à vie** — jamais dérivée du mot de passe (cf.
// decisions-fondatrices.md §A.1). Le mot de passe ne fait que l'envelopper :
//
//   mot de passe ──OPAQUE──► exportKey ──HKDF──► KEK ──enveloppe──► K
//
// Le serveur ne conserve que K enveloppée. N'ayant pas le mot de passe, il ne
// peut pas l'ouvrir. Et comme K ne change jamais, changer de mot de passe ne
// demande qu'une ré-enveloppe : les parts Shamir déjà déposées (dont celle du
// bâtonnier) restent valides.

import { api } from '../api/client';
import {
  deriveKek,
  generateMasterKey,
  loadCrypto,
  masterKeyFingerprint,
  unwrapMasterKey,
  wrapMasterKey,
} from './index';

const toBase64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));

const fromBase64 = (b64: string) =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

/**
 * L'`exportKey` d'OPAQUE arrive sous forme de chaîne encodée. On la passe
 * telle quelle à HKDF plutôt que de la décoder : l'encodage est bijectif, donc
 * l'entropie est intégralement préservée, et on évite de dépendre du format
 * exact (hex ou base64) retenu par la lib.
 */
const exportKeyBytes = (exportKey: string) => new TextEncoder().encode(exportKey);

export type MasterKeyResult = {
  masterKey: Uint8Array;
  /** Vrai si K vient d'être créée (premier enrôlement de cet utilisateur). */
  created: boolean;
  /** Empreinte publique de K — ne la révèle pas, sert à la vérifier. */
  fingerprint: Uint8Array;
};

/**
 * Récupère K, ou la crée si l'utilisateur n'en a pas encore.
 *
 * - Déjà enrôlée → on déballe l'enveloppe rendue par le serveur. Un mot de
 *   passe erroné ferait échouer l'AEAD… mais ce cas ne peut pas se produire
 *   ici : OPAQUE a déjà refusé le login en amont.
 * - Pas encore enrôlée → on tire 256 bits d'aléa, on enveloppe, on dépose.
 *   Le serveur refuse tout écrasement ultérieur (409).
 */
export async function unlockMasterKey(
  sessionToken: string,
  exportKey: string,
): Promise<MasterKeyResult> {
  await loadCrypto();

  const kek = deriveKek(exportKeyBytes(exportKey));

  const stored = await api<{ encryptedMasterKey: string | null }>('/keys/master', {
    token: sessionToken,
  });

  if (stored.encryptedMasterKey) {
    const masterKey = unwrapMasterKey(kek, fromBase64(stored.encryptedMasterKey));
    return { masterKey, created: false, fingerprint: masterKeyFingerprint(masterKey) };
  }

  const masterKey = generateMasterKey();
  await api('/keys/master', {
    method: 'PUT',
    token: sessionToken,
    body: { encryptedMasterKey: toBase64(wrapMasterKey(kek, masterKey)) },
  });

  return { masterKey, created: true, fingerprint: masterKeyFingerprint(masterKey) };
}

/**
 * Ré-enveloppe **la même** clé maîtresse avec la KEK d'un nouveau mot de passe.
 *
 * C'est tout ce qu'un changement de mot de passe implique côté crypto : K ne
 * bouge pas, donc rien n'est à re-chiffrer et les parts Shamir déjà déposées
 * (dont celle du bâtonnier) restent valides.
 */
export async function rewrapMasterKey(
  masterKey: Uint8Array,
  newExportKey: string,
): Promise<string> {
  await loadCrypto();
  const kek = deriveKek(exportKeyBytes(newExportKey));
  return toBase64(wrapMasterKey(kek, masterKey));
}
